import type { Blob2DInPatchUnits } from './Blob2DInPatchUnits';

export type ThresholdAndConnectedComponentsArgs = {
  heatmap: Float32Array;
  gridH: number;
  gridW: number;
  threshold: number;
  // When true, each returned blob carries `patchIndices` (flat indices into
  // the heatmap) for every patch in its component. Required to fit an
  // oriented bounding box via PCA. Default `false` to keep the common
  // axis-aligned path allocation-free per blob.
  collectPatches?: boolean;
};

// 4-connectivity flood-fill over patches with score >= threshold, returning
// every connected component. Iterative stack to handle large blobs without
// blowing the JS call stack. No size filtering — match area is enforced in
// pixel space downstream (see runLocate).
export function thresholdAndConnectedComponents(
  args: ThresholdAndConnectedComponentsArgs,
): Blob2DInPatchUnits[] {
  const { heatmap, gridH, gridW, threshold, collectPatches } = args;

  if (heatmap.length !== gridH * gridW) {
    throw new Error(
      `thresholdAndConnectedComponents: heatmap length ${heatmap.length} ` +
        `!= gridH*gridW ${gridH * gridW}`,
    );
  }

  const visited = new Uint8Array(gridH * gridW);
  const blobs: Blob2DInPatchUnits[] = [];
  const stack: number[] = [];

  for (let row = 0; row < gridH; row += 1) {
    for (let col = 0; col < gridW; col += 1) {
      const startIdx = row * gridW + col;
      if (visited[startIdx]) continue;

      if (heatmap[startIdx] < threshold) {
        visited[startIdx] = 1;
        continue;
      }

      let minR = row;
      let maxR = row;
      let minC = col;
      let maxC = col;
      let maxScore = heatmap[startIdx];
      let minScore = heatmap[startIdx];
      let count = 0;

      // Optional patch buffer for oriented-box fitting. Sized to the
      // remaining-grid budget so a worst-case "everything connected" blob
      // fits without growing.
      const patches = collectPatches
        ? new Uint32Array(gridH * gridW - row * gridW - col)
        : null;

      stack.length = 0;
      stack.push(startIdx);
      visited[startIdx] = 1;

      while (stack.length > 0) {
        const idx = stack.pop() as number;
        const r = Math.floor(idx / gridW);
        const c = idx - r * gridW;
        const s = heatmap[idx];
        if (s > maxScore) maxScore = s;
        if (s < minScore) minScore = s;
        if (r < minR) minR = r;
        if (r > maxR) maxR = r;
        if (c < minC) minC = c;
        if (c > maxC) maxC = c;
        if (patches) patches[count] = idx;
        count += 1;

        // 4-connectivity: N, S, W, E. Diagonals deliberately excluded.
        if (r > 0) {
          const n = idx - gridW;

          if (!visited[n] && heatmap[n] >= threshold) {
            visited[n] = 1;
            stack.push(n);
          } else if (!visited[n]) {
            visited[n] = 1;
          }
        }

        if (r < gridH - 1) {
          const n = idx + gridW;

          if (!visited[n] && heatmap[n] >= threshold) {
            visited[n] = 1;
            stack.push(n);
          } else if (!visited[n]) {
            visited[n] = 1;
          }
        }

        if (c > 0) {
          const n = idx - 1;

          if (!visited[n] && heatmap[n] >= threshold) {
            visited[n] = 1;
            stack.push(n);
          } else if (!visited[n]) {
            visited[n] = 1;
          }
        }

        if (c < gridW - 1) {
          const n = idx + 1;

          if (!visited[n] && heatmap[n] >= threshold) {
            visited[n] = 1;
            stack.push(n);
          } else if (!visited[n]) {
            visited[n] = 1;
          }
        }
      }

      blobs.push({
        x: minC,
        y: minR,
        width: maxC - minC + 1,
        height: maxR - minR + 1,
        score: maxScore,
        minScore,
        patchCount: count,
        // Slice down to actual count so consumers can iterate with .length.
        patchIndices: patches ? patches.slice(0, count) : undefined,
      });
    }
  }

  return blobs;
}
